import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCanonical } from '@/hooks/useCanonical';
import { Gift, Clock, ArrowRight, CheckCircle2, CalendarDays, Phone, CreditCard, Heart } from 'lucide-react';
import { services, formules, formulesEpilation, PLANITY_URL } from '@/data/services';
import CTASection from '@/components/CTASection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const faqItems = [
  {
    q: 'Comment fonctionne la carte cadeau Body by CC ?',
    a: (
      <div className="space-y-4">
        <p>Vous choisissez la prestation ou la formule que vous souhaitez offrir, et vous recevez un <strong>PDF personnalisé avec un code unique</strong>.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
            <Gift size={20} className="text-primary mx-auto mb-2" />
            <p className="font-heading font-bold text-sm text-foreground">1. Choisissez</p>
            <p className="text-xs text-muted-foreground mt-1">Soin, formule ou montant libre</p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
            <CreditCard size={20} className="text-primary mx-auto mb-2" />
            <p className="font-heading font-bold text-sm text-foreground">2. Recevez le PDF</p>
            <p className="text-xs text-muted-foreground mt-1">Code unique de réservation</p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
            <Heart size={20} className="text-primary mx-auto mb-2" />
            <p className="font-heading font-bold text-sm text-foreground">3. Offrez !</p>
            <p className="text-xs text-muted-foreground mt-1">Un moment de bien-être inoubliable</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
          <CalendarDays size={16} className="text-accent shrink-0" />
          <p className="text-sm"><strong>Validité :</strong> 6 mois après la date d'achat.</p>
        </div>
      </div>
    ),
  },
  {
    q: 'Peut-on offrir n\'importe quelle prestation ?',
    a: (
      <div className="space-y-4">
        <p><strong>Oui, absolument !</strong> Toutes nos prestations et formules sont disponibles en carte cadeau :</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Soins visage</strong> — Hydratation, anti-âge, peeling, Skincleantech</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Technologies premium</strong> — HIFU lifting, E-SLIM Cryo 360°</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Massages & détente</strong> — Relaxant, drainant, pierres chaudes</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Épilation & beauté</strong> — Cire, fil, manucure, blanchiment dentaire</span></li>
        </ul>
        <p className="text-sm italic text-muted-foreground/80">Vous pouvez aussi offrir nos <Link to="/formules" onClick={() => window.scrollTo(0, 0)} className="text-primary hover:underline">formules combinées</Link> à tarif préférentiel.</p>
      </div>
    ),
  },
  {
    q: 'Comment le bénéficiaire utilise sa carte cadeau ?',
    a: (
      <div className="space-y-4">
        <p>C'est très simple ! Le bénéficiaire suit ces étapes :</p>
        <ol className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 font-heading font-bold text-xs text-primary">1</span>
            <span>Il reçoit le <strong>PDF avec le code de réservation</strong> (par email ou imprimé)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 font-heading font-bold text-xs text-primary">2</span>
            <span>Il prend rendez-vous en ligne sur <a href={PLANITY_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">Planity</a> ou par téléphone</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 font-heading font-bold text-xs text-primary">3</span>
            <span>Il se présente à l'institut avec son code et profite de son soin !</span>
          </li>
        </ol>
        <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/10 p-3">
          <Phone size={16} className="text-primary shrink-0" />
          <p className="text-sm">Besoin d'aide ? Appelez le <a href="tel:+33645230034" className="text-primary hover:underline font-medium">06 45 23 00 34</a></p>
        </div>
      </div>
    ),
  },
  {
    q: 'Y a-t-il une durée de validité ?',
    a: (
      <div className="space-y-4">
        <p>Oui, la carte cadeau est <strong>valable 6 mois</strong> après la date d'achat.</p>
        <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
          <CalendarDays size={16} className="text-accent shrink-0" />
          <div>
            <p className="text-sm font-medium">Durée de validité : 6 mois</p>
            <p className="text-xs text-muted-foreground mt-0.5">Passé ce délai, la carte ne pourra plus être utilisée. Pensez à réserver suffisamment tôt !</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">Nous vous recommandons de prendre rendez-vous au moins <strong>2 semaines avant l'expiration</strong> pour garantir la disponibilité du créneau souhaité.</p>
      </div>
    ),
  },
  {
    q: 'Puis-je offrir une formule combinée ?',
    a: (
      <div className="space-y-4">
        <p><strong>Oui !</strong> Toutes nos formules avantageuses sont disponibles en carte cadeau :</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { name: 'Découverte Visage', price: '110 €', desc: 'Soin complet + Skincleantech' },
            { name: 'Peau Neuve', price: '110 €', desc: 'Peeling + LED + masque' },
            { name: 'Beauté Express', price: '70 €', desc: 'Manucure + soin rapide' },
            { name: 'Corps Sculpté', price: '60 €', desc: 'Minceur + drainage' },
          ].map((f) => (
            <div key={f.name} className="rounded-xl bg-primary/5 border border-primary/10 p-3">
              <p className="font-heading font-bold text-sm text-foreground">{f.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{f.desc}</p>
              <p className="font-heading font-bold text-accent text-sm mt-1">{f.price}</p>
            </div>
          ))}
        </div>
        <Link to="/formules" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline">
          Voir toutes les formules <ArrowRight size={14} />
        </Link>
      </div>
    ),
  },
];

const Offrir = () => {
  useCanonical('/offrir');
  useEffect(() => {
    document.title = 'Offrir une carte cadeau bien-être — Body by CC | Sevran (93)';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Offrez un moment de bien-être à un proche. Carte cadeau prestige Body by CC valable 6 mois : soins visage, HIFU, Cryo 360°, massages, épilation. Institut à Sevran.');
  }, []);

  const allCategories = [
    { title: 'Formules', prestations: formules },
    ...services.map((s) => ({ title: s.title, prestations: s.prestations, supplements: s.supplements })),
    { title: 'Formules d\'épilation cire & fil', prestations: formulesEpilation },
  ];

  return (
    <>
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-lg bg-primary/20 flex items-center justify-center">
              <Gift size={22} className="text-primary" />
            </div>
            <span className="text-xs font-heading font-medium text-accent uppercase tracking-[0.2em]">Carte cadeau</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold leading-tight">Offrez un moment de bien-être</h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-2xl text-base md:text-lg leading-relaxed font-body">
            Faites plaisir à un proche en lui offrant un soin Body by CC. Choisissez parmi toutes nos prestations et formules — du massage détente au HIFU lifting.
            Vous recevrez un <strong className="text-petrol-foreground/80">PDF avec un code de réservation</strong> utilisable en institut.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-heading font-medium text-base hover:bg-primary/90 transition-colors"
            >
              Offrir une carte cadeau <ArrowRight size={16} />
            </a>
            <a href="tel:+33645230034" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-petrol-foreground/20 text-petrol-foreground font-heading font-medium text-base hover:bg-petrol-foreground/10 transition-colors">
              <Phone size={16} /> 06 45 23 00 34
            </a>
          </div>
        </div>
      </section>

      {/* Info banner */}
      <section className="px-6 lg:px-8 py-8 bg-primary/10 border-b border-border">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Gift size={20} className="text-primary shrink-0" />
          <p className="text-sm font-body text-foreground">
            <span className="font-heading font-semibold">Carte prestige</span> — valable 6 mois après la date d'achat. Le montant ou la prestation offerte dépendra du soin ou de la formule choisie.
          </p>
        </div>
      </section>

      {/* All categories */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-10">Toutes les cartes cadeaux</h2>

          <div className="space-y-12">
            {allCategories.map((cat) => (
              <div key={cat.title}>
                <h3 className="font-heading text-lg font-bold text-foreground mb-4 pb-2 border-b border-border">{cat.title}</h3>
                <div className="space-y-2">
                  {cat.prestations.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-border bg-card hover:border-primary/20 transition-colors">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading font-semibold text-sm text-foreground">{p.name}</h4>
                        {p.description && <p className="text-xs text-muted-foreground mt-1 font-body line-clamp-1">{p.description}</p>}
                      </div>
                      <div className="flex items-center gap-4 shrink-0 ml-4">
                        <span className="text-xs text-muted-foreground flex items-center gap-1 font-body"><Clock size={12} /> {p.duration}</span>
                        <span className="font-heading font-bold text-accent text-sm min-w-[60px] text-right">{p.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {'supplements' in cat && cat.supplements && (
                  <div className="mt-3 px-4">
                    <p className="text-xs font-heading font-semibold text-muted-foreground mb-1">Suppléments :</p>
                    {(cat.supplements as string[]).map((s, i) => (
                      <p key={i} className="text-xs text-muted-foreground font-body">• {s}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">FAQ</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Questions fréquentes — Cartes cadeaux</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-2xl px-6 md:px-8 bg-background data-[state=open]:border-primary/30 data-[state=open]:shadow-sm transition-all">
                <AccordionTrigger className="font-heading text-base md:text-lg font-semibold text-foreground hover:no-underline py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-6 font-body">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base md:text-lg hover:underline"
            >
              Voir toutes les questions <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        title="Offrez un moment de bien-être"
        subtitle="Faites plaisir à un proche avec une carte cadeau Body by CC. Soins visage, corps, massages, HIFU et plus encore."
        buttonLabel="Offrir un soin"
        buttonLink={PLANITY_URL}
        isExternal
      />
    </>
  );
};

export default Offrir;
